import {
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast, toastError } from '@/components/ui/toast';
import { useCustodialBalance } from '@/features/custodial/hooks/useCustodialBalance';
import { useCustodialJobs } from '@/features/custodial/hooks/useCustodialJobs';
import { useCustodialPanelState } from '@/features/custodial/hooks/useCustodialPanelState';
// from the defining module, not useCustodialJobs' re-export: this is a pure predicate and
// pulling it through the hook module would drag the hook into anything that mocks it.
import { isActiveSpeedJob } from '@/features/custodial/jobStatus';
import {
    HIP3_SETUP_FUNDING_REQUIRED_CODE,
    HYPERCORE_BOOTSTRAP_MINIMUM_USDC,
    isActivationFundingRequired,
    useActivationFundingBlock,
} from '@/features/custodial/snappyActivation';
import { runWithFundAuthorization } from '@/features/fundAuthorization';
import { useAuthedApi, useSpeedBackend, useWallet } from '@/features/wallet';
import {
    type AgentStatusResponse,
    type SpeedJobResponse,
    reactivateAgent,
} from '@/lib/api/speed';
import { isApiError } from '@/lib/errors';
import { queryKeys } from '@/lib/query';

import { selectIsRealMode, useTradingStore } from '../store';

// the status is a cheap db read; refetch on mount and on window focus (the focus refetch is
// switched on per-query below — the app-wide default is `refetchOnWindowFocus: false`) but keep a
// short stale window so opening the trade view does not spam the endpoint.
const AGENT_STATUS_STALE_TIME_MS = 30_000;

/**
 * Mirrors the backend's agent re-approval margin (MoonHub.API flips `needsReactivation` once the
 * approval is within this window of `validUntil`). Only used to schedule ONE local invalidation at
 * that boundary so a long-lived tab notices the flip without polling.
 */
const AGENT_REAPPROVAL_MARGIN_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Ceiling for that one-shot timer. `validUntil` is issued ~180 days out, so the raw delay
 * overflows setTimeout's 2^31 ms limit (which fires IMMEDIATELY, in a loop). Only arm the timer
 * once the boundary is within a day; a tab open that long is the only case it can help.
 */
const AGENT_STATUS_BOUNDARY_TIMER_MAX_MS = 24 * 60 * 60 * 1000;
// job statuses that mean the work is over, either way. anything else is still running and must keep
// the UI in its pending state.
const TERMINAL_JOB_STATUSES = ['settled', 'failed'];

const REACTIVATE_ERROR_TOAST_MS = 10_000;

/**
 * What the user is told when activation cannot proceed without a deposit. Same instruction as the
 * banner, so the toast and the panel do not disagree.
 */
const DEPOSIT_TO_ACTIVATE_MESSAGE =
    'Activation needs at least 2 USDC in your Snappy Wallet. Deposit to finish setting it up.';

/**
 * The browser wallet flow (V2) has no perps wallet to reactivate: its "activation" is the builder fee and the
 * unified account, signed in the browser by the held key. Same words as the V2 banner's own approval.
 */
const V2_ACTIVATION_DONE_MESSAGE = 'Trading approved.';

/** A parked setup that needs the user's signature again, after a reload or a declined prompt. */
const AWAITING_SIGNATURE_MESSAGE =
    'Your Snappy Wallet still needs your signature to finish activating. Press Activate to continue.';

/**
 * An agentSetup job that only a deposit can move: a `failed` job carrying the funding code.
 *
 * `authorizationRequired` is deliberately NOT one. It is the backend's normal park while the
 * fund-authorization handshake is in flight (the job row sits there between the challenge and the
 * user's signature), and a first-time run parks there a second time after creating the adapter.
 * Reading it as a funding dead end is what opened the deposit panel over a user who had just
 * signed, with a "deposit to activate" toast for a wallet holding plenty. What it needs is the
 * next round of the same request, which the mutation below runs on its own; after a reload it
 * needs the user to press the button again, and that is said as such.
 */
function isSetupDeadEnd(job: SpeedJobResponse): boolean {
    return (
        job.status === 'failed' && job.code === HIP3_SETUP_FUNDING_REQUIRED_CODE
    );
}

/**
 * Extra rounds of the reactivate request one press may need: the first-time run creates the
 * adapter, parks for the bootstrap authorization, and only then can the descriptors be issued.
 */
const AUTHORIZATION_ROUNDS = 3;

/**
 * Deposit jobs whose settlement this page load has already turned into an automatic first
 * activation, and the statuses each deposit job was last seen in. Module level: the hook is
 * mounted from ~8 places and exactly one of them may fire the activation for a given deposit.
 */
const observedDepositStatuses = new Map<string, string>();
const autoActivatedDeposits = new Set<string>();
/**
 * One reactivate mutation at a time, across every instance of this hook. Two instances arming
 * the same first activation (the deposit flow in one, the jobs poll in another) fired two POSTs
 * 5 ms apart in prod: one got the signing challenge, the other the job row, and the job then
 * parked waiting for proofs the first round never sent. `useIsMutating` is a render-time value,
 * so it cannot fence two effects firing in the same tick; this flag can.
 */
let reactivationInFlight = false;
/** When a parked job was last resumed from here, so the stale parked row the poll still holds is ignored. */
const resumedParksAtMs = new Map<string, number>();
/** Automatic resumes per parked job in this page load, and when the last one went out. */
const parkResumeAttempts = new Map<string, number>();
const parkResumeLastAtMs = new Map<string, number>();
const PARK_RESUME_MAX_ATTEMPTS = 3;
const PARK_RESUME_RETRY_MS = 20_000;
/** How long after a deposit settles the balance read may take to show the funds. */
const AUTO_ACTIVATION_WINDOW_MS = 90_000;

/**
 * Shared mutation identity. The in-flight state has to be visible to surfaces that did NOT fire
 * the mutation — the trade buttons, the deposit/withdraw panel — and a per-hook `isPending` is
 * not, since each caller gets its own mutation instance. Keying it lets `useIsMutating` report
 * the same answer everywhere without a parallel store.
 */
const REACTIVATE_MUTATION_KEY = ['speed', 'agent', 'reactivate'] as const;

/**
 * Setup jobs whose outcome has already been announced, keyed by job id (`release:<id>` for the
 * safety release). Module level on purpose: this hook has ~8 live instances, and every one of them
 * ADOPTS a running setup job (that is what makes the outcome survive a reload), so without a
 * shared record the user would get one toast per mounted instance. The cache invalidations are
 * deliberately left outside this guard — they are idempotent and cheap.
 */
const reportedSetupJobOutcomes = new Set<string>();

/** test-only: clears the announced-outcome record so a suite starts from a known state. */
export function resetReportedSetupJobOutcomes(): void {
    reportedSetupJobOutcomes.clear();
    observedDepositStatuses.clear();
    autoActivatedDeposits.clear();
    resumedParksAtMs.clear();
    parkResumeAttempts.clear();
    parkResumeLastAtMs.clear();
    reactivationInFlight = false;
}

/**
 * How long agent setup usually takes end to end: a service-wallet transaction on HyperEVM plus
 * HyperCore confirming the agent. An ESTIMATE for the progress bar only — nothing waits on it and
 * exceeding it is not a failure.
 */
export const REACTIVATION_ESTIMATE_MS = 15_000;

/** Slack on top of the estimate before a still-running setup counts as stalled. */
const REACTIVATION_STALL_MARGIN_MS = 3 * 60_000;

/**
 * Ceiling on how long ANY reactivation may hold the UI in its pending state, whether the job is
 * merely watched or reported active by the server. The job list is the authority for "still
 * running", but a job wedged in `queued` (or one that never appears in the list at all) would
 * otherwise disable trading forever. Past this the UI re-arms with a retry or, when the wallet is
 * short of the bootstrap minimum, the deposit instruction: either beats a spinner with no exit.
 */
const REACTIVATION_STALL_AFTER_MS =
    REACTIVATION_ESTIMATE_MS + REACTIVATION_STALL_MARGIN_MS;

interface UseAgentReactivationResult {
    /** True only in real mode when the agent must be (re-)approved before trading. */
    needsReactivation: boolean;
    /**
     * True only in real mode when the user has no speed wallet at all. The backend keeps
     * `needsReactivation` true in that state, but there is nothing to reactivate yet — the
     * reactivate POST throws `tradeSetupRequired` — so onboarding surfaces must gate on this.
     */
    walletMissing: boolean;
    bootstrapRequired: boolean;
    /**
     * True when activation cannot succeed until the user deposits: the HyperCore bootstrap is
     * still part of the run and the Snappy Wallet holds less than the minimum. `reactivate` is a
     * no-op in this state (it opens the deposit panel instead), so no surface can fire a POST
     * that is guaranteed to fail.
     */
    activationFundingRequired: boolean;
    /** True when the reactivation is only needed because the builder fee rate changed. */
    builderApprovalStale: boolean;
    agentAddress: string | null;
    validUntil: number | null;
    /** No agent was ever approved for this wallet: the first press activates, not re-activates. */
    firstActivation: boolean;
    /** A setup run is parked on the user's signature; pressing the button resumes it. */
    awaitingSignature: boolean;
    /** Deposit flow hook: a deposit just settled, activate as soon as the funds show. */
    armFirstActivation: () => void;
    isRealMode: boolean;
    /**
     * True while a reactivation is running — the POST, the watched job, OR any active
     * `agentSetup` job the server reports. That last term is what makes this survive a reload:
     * the local state is gone after F5, the job is not.
     *
     * Shared across every caller, so surfaces that must not be used mid-setup (long/short,
     * deposit/withdraw) can gate on it directly.
     */
    isReactivating: boolean;
    /**
     * Epoch ms the running reactivation started, for the progress estimate. Taken from the
     * job's `createdAt` when one is visible so the bar resumes at the right point after a
     * reload rather than restarting from zero. Null when nothing is running.
     */
    reactivationStartedAtMs: number | null;
    reactivate: () => void;
    /**
     * True only in real mode when the account still needs the one-time HIP-3
     * (unified-account) activation before its first builder-market open.
     */
    requiresHip3Activation: boolean;
    /** True while the long-running activate-hip3 POST is in flight. */
    isActivatingHip3: boolean;
    activateHip3: () => void;
}

/**
 * Drives the "Reactivate perps wallet" and "Activate HIP-3 markets" banners.
 * Reads `GET /speed/agent/status` (real mode only) to decide whether the user's
 * Hyperliquid agent needs a proactive re-approval and whether the account still
 * needs the one-time HIP-3 activation, and exposes the matching long-running
 * mutations (`POST /speed/agent/reactivate`, `POST /speed/agent/activate-hip3`).
 * On success the status cache is refreshed so the banners self-clear.
 *
 * The pruned-agent case (an `AGENT_REACTIVATION_REQUIRED` thrown at order time,
 * which the status read does NOT reflect) and the first-open HIP-3 case
 * (`HIP3_ACTIVATION_REQUIRED`) are surfaced by seeding this same status query
 * from the trade-error handler.
 */
export function useAgentReactivation(): UseAgentReactivationResult {
    // the reactivation runs the V1 fund-authorization ceremony, which needs the raw MoonHub
    // client; the status read and the HIP-3 activation go through the backend seam.
    const authedApi = useAuthedApi();
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    // the job the last reactivate started, watched until it settles or fails. state, not a ref:
    // the pending UI is derived from it, so it has to re-render.
    const [watchedJobId, setWatchedJobId] = useState<string | null>(null);
    const { jobs } = useCustodialJobs();
    const isRealMode = useTradingStore(selectIsRealMode);

    const statusQuery = useQuery({
        queryKey: queryKeys.speed.agentStatus(),
        queryFn: async ({ signal }) => backend!.fetchAgentStatus(signal),
        enabled: isRealMode && !!backend,
        staleTime: AGENT_STATUS_STALE_TIME_MS,
        // per-query override of the app-wide `refetchOnWindowFocus: false`. A tab left open across
        // an expiry (or across a reactivation done elsewhere) otherwise keeps showing the state it
        // read on mount, and this read is a single cheap db hit.
        refetchOnWindowFocus: true,
    });

    const { address } = useWallet();
    const status = statusQuery.data;

    const { numericBalance, isLoading: isBalanceLoading } =
        useCustodialBalance();
    const isFundingBlockedByBackend = useActivationFundingBlock(
        (state) => state.isBlocked,
    );
    const markFundingBlocked = useActivationFundingBlock(
        (state) => state.markBlocked,
    );
    const openDeposit = useCustodialPanelState((state) => state.openDeposit);

    /**
     * The one rule every surface reads: can this activation succeed at all right now? A
     * never-approved agent still has the HyperCore bootstrap ahead of it, and that bootstrap moves
     * USDC out of the Snappy Wallet, so under the minimum the run dies with
     * HIP3_SETUP_FUNDING_REQUIRED (newer backends reject the POST outright, older ones only find
     * out inside the job).
     */
    // V1 only: the bootstrap moves USDC out of the custodial wallet. A browser wallet (V2) activates by signing,
    // whatever it holds.
    const activationFundingRequired =
        isRealMode &&
        backend?.kind !== 'v2' &&
        isActivationFundingRequired({
            agentAddress: status?.agentAddress ?? null,
            validUntil: status?.validUntil ?? null,
            bootstrapRequired: status?.bootstrapRequired ?? false,
            agentStatusKnown: status !== undefined,
            numericBalance,
            isBalanceLoading,
            fundingBlockedByBackend: isFundingBlockedByBackend,
        });
    // read inside timers and effect closures that must not re-arm on every balance tick
    const activationFundingRequiredRef = useRef(activationFundingRequired);
    activationFundingRequiredRef.current = activationFundingRequired;

    // when this tab fired the mutation but the job list has not caught up yet (~1 poll interval),
    // so the progress bar has a start time from the very first frame instead of jumping.
    const optimisticStartRef = useRef<number | null>(null);
    const isReactivatingRef = useRef(false);

    const { mutate, isPending } = useMutation({
        mutationKey: REACTIVATE_MUTATION_KEY,
        onMutate: () => {
            reactivationInFlight = true;
            optimisticStartRef.current = Date.now();
        },
        onSettled: () => {
            reactivationInFlight = false;
        },
        mutationFn: async () => {
            // V2 (browser wallet): the builder fee and the unified account are signed right here by the held key.
            // No fund authorization (nothing leaves the account) and no job to watch: the call answers when the
            // activation is done, or throws the exchange's refusal as plain copy.
            if (backend?.kind === 'v2') {
                return backend.reactivateAgent();
            }
            if (!authedApi) {
                throw new Error('Not authenticated.');
            }
            // reactivation can trigger the one-time HyperCore bootstrap, which signs an
            // approve_erc20 and an hl_fund — both fund operations, so RustSigner requires a fresh
            // user proof. the first attempt goes out with an empty map; only if the backend answers
            // with descriptors does the user see a single signing prompt. a reactivation that needs
            // no bootstrap never prompts, so ordinary trading is unaffected.
            const request = async () =>
                runWithFundAuthorization(
                    async (fundAuthorizations) =>
                        reactivateAgent(authedApi, fundAuthorizations),
                    {
                        connectedAddress: address,
                        // the bootstrap amount is derived by the server from the live custodial
                        // balance, so there is no client-side amount to bind; the slot is
                        // server-derived and the ceremony checks owner, chain and window instead.
                        amount: 0n,
                        flow: 'agent setup',
                    },
                );
            let job = await request();
            // a job parked in authorizationRequired is the same request asking for its next
            // round (the adapter now exists, so the bootstrap descriptors can be issued), not an
            // outcome to report. Bounded, so a backend that keeps parking cannot loop the user.
            for (
                let round = 0;
                round < AUTHORIZATION_ROUNDS &&
                job.status === 'authorizationRequired';
                round++
            ) {
                job = await request();
            }
            return job;
        },
        onSuccess: (job) => {
            if (backend?.kind === 'v2') {
                // the status and the balance are Hyperliquid's own answers now: read them again so the banner
                // clears and the unified account's collateral shows.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.balance(),
                });
                toast.success(V2_ACTIVATION_DONE_MESSAGE);
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.agentStatus(),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.jobs(),
            });

            // a terminal answer is the only thing worth reporting as done.
            if (job.status === 'settled') {
                toast.success('Perps wallet reactivated.');
                return;
            }
            if (job.status === 'failed') {
                if (job.code === HIP3_SETUP_FUNDING_REQUIRED_CODE) {
                    markFundingBlocked();
                    openDeposit();
                }
                // `job.error` is backend text: the presenter passes a plain
                // sentence through and replaces anything technical.
                toastError(job.error || 'Perps wallet setup failed.', {
                    operation: 'Reactivation',
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
                return;
            }
            // still parked after every round this press was allowed: the user's signature is what
            // it waits for, and pressing the button again is how they give it. Never the deposit
            // panel: nothing about this state is about funds.
            if (job.status === 'authorizationRequired') {
                toast.error(AWAITING_SIGNATURE_MESSAGE, {
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
                return;
            }

            // QUEUED is not success. The POST only enqueues; setup can still die minutes later
            // (HIP3_SETUP_FUNDING_REQUIRED, a reverting approveBuilderFee). Reporting it green and
            // re-enabling the button let the user fire the same job repeatedly while the first one was
            // still running. Hold the pending state until the job actually reaches a terminal status.
            setWatchedJobId(job.jobId);
        },
        onError: (error) => {
            console.warn(
                '[AgentReactivation] reactivate request failed',
                error,
            );
            if (backend?.kind === 'v2') {
                // a signature the exchange refused, or a locked wallet: the presenter has the plain copy.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                });
                toastError(error, {
                    operation: 'Approval',
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
                return;
            }
            // newer backends refuse the setup outright below the bootstrap minimum (a 422 today,
            // so this matches on the CODE and not the status, exactly like
            // AGENT_REACTIVATION_REQUIRED). That is not a failure to report as one: it is the
            // deposit instruction, and the FE may not have known
            // it was needed (an unread balance, or an older status that never raised
            // `bootstrapRequired`). Remember it so every surface switches to the deposit ask.
            if (
                isApiError(error) &&
                error.body?.code === HIP3_SETUP_FUNDING_REQUIRED_CODE
            ) {
                markFundingBlocked();
                openDeposit();
                toast.error(DEPOSIT_TO_ACTIVATE_MESSAGE, {
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
                return;
            }
            // everything else goes through the presenter: the fund-authorization
            // handshake, a schema mismatch and a 502 all used to arrive here as
            // raw technical text.
            toastError(error, {
                operation: 'Reactivation',
                duration: REACTIVATE_ERROR_TOAST_MS,
            });
        },
    });

    // Holds the pending state until the watched job REACHES a terminal status, then reports what
    // actually happened. The jobs list already polls every few seconds while a job is active, so this
    // only reads it.
    // shared across every caller of this hook, unlike the per-instance `isPending`
    const isMutatingReactivation =
        useIsMutating({ mutationKey: REACTIVATE_MUTATION_KEY }) > 0;

    useEffect(() => {
        if (!watchedJobId) return;
        const job = jobs.find((candidate) => candidate.jobId === watchedJobId);
        if (!job) return;
        // the handshake park, seen by the poll while no request is in flight any more: the run
        // stopped waiting for a signature it never got (declined, or the tab was reloaded). It is
        // not over and not a funding problem; release the pending state and say what it needs.
        if (job.status === 'authorizationRequired') {
            if (isMutatingReactivation || reactivationInFlight) return;
            // the resume request answered with a newer row, but the jobs cache keeps the parked one
            // until its next poll: a row no newer than the resume is that stale copy, not a new park
            const resumedAt = resumedParksAtMs.get(job.jobId);
            if (
                resumedAt !== undefined &&
                Date.parse(job.updatedAt) <= resumedAt
            )
                return;
            // release the pending state; the parked-job effect below resumes it on its own (the
            // next request answers with the signing challenge, so the user sees the wallet prompt,
            // not a toast telling them to press a button) and only hands it back to the user once
            // its attempts are spent.
            setWatchedJobId(null);
            return;
        }
        const deadEnd = isSetupDeadEnd(job);
        if (!deadEnd && !TERMINAL_JOB_STATUSES.includes(job.status)) return;

        setWatchedJobId(null);
        // a job the client cannot carry further is not "still setting up": remember the block so
        // the banner turns into the deposit instruction instead of a spinner with no exit.
        if (deadEnd) {
            markFundingBlocked();
        }
        // one announcement per job, no matter how many hook instances are watching it
        if (!reportedSetupJobOutcomes.has(job.jobId)) {
            reportedSetupJobOutcomes.add(job.jobId);
            if (job.status === 'settled') {
                toast.success('Perps wallet reactivated.');
            } else if (deadEnd) {
                openDeposit();
                toastError(job.error || DEPOSIT_TO_ACTIVATE_MESSAGE, {
                    operation: 'Reactivation',
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
            } else {
                toastError(
                    job.error ||
                        'Snappy Wallet setup could not finish. Please try again.',
                    {
                        operation: 'Reactivation',
                        duration: REACTIVATE_ERROR_TOAST_MS,
                    },
                );
            }
        }
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.agentStatus(),
        });
    }, [
        jobs,
        watchedJobId,
        queryClient,
        markFundingBlocked,
        openDeposit,
        isMutatingReactivation,
    ]);

    // Safety release. Everything above depends on the job appearing in the polled list and reaching
    // a status; if it never does (the list errors, the job is pruned, the job simply wedges) the
    // button would stay disabled forever, which is worse than re-enabling. Bounded by the setup
    // estimate plus a margin, so it cannot fire during a normal (even slow) run.
    useEffect(() => {
        if (!watchedJobId) return;
        const releasedJobId = watchedJobId;
        const timer = setTimeout(() => {
            setWatchedJobId(null);
            // same one-announcement-per-job rule as the terminal effect; the key is namespaced so
            // a later real outcome for the same job can still be reported.
            if (reportedSetupJobOutcomes.has(`release:${releasedJobId}`))
                return;
            reportedSetupJobOutcomes.add(`release:${releasedJobId}`);
            toast.error(
                activationFundingRequiredRef.current
                    ? DEPOSIT_TO_ACTIVATE_MESSAGE
                    : 'Still working on your Snappy Wallet, check back shortly before retrying.',
                { duration: REACTIVATE_ERROR_TOAST_MS },
            );
        }, REACTIVATION_STALL_AFTER_MS);
        return () => clearTimeout(timer);
    }, [watchedJobId]);

    const { mutate: mutateActivateHip3, isPending: isActivatingHip3 } =
        useMutation({
            mutationFn: async () => {
                if (!backend) {
                    throw new Error('Not authenticated.');
                }
                return backend.activateHip3();
            },
            onSuccess: () => {
                // optimistically clear the banner; the invalidation reconciles
                // the authoritative activation state from the backend.
                queryClient.setQueryData<AgentStatusResponse>(
                    queryKeys.speed.agentStatus(),
                    (prev) =>
                        prev && { ...prev, requiresHip3Activation: false },
                );
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                });
                toast.success('HIP-3 markets activated.');
            },
            onError: (error) => {
                toastError(error, {
                    operation: 'Market activation',
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
            },
        });

    const reactivate = useCallback(() => {
        if (isPending) return;
        // the POST cannot succeed under the bootstrap minimum, and firing it anyway is what left
        // the user staring at "Setting up your wallet" forever. Send them to the deposit instead,
        // whichever surface asked (the banner, or a trade that failed with
        // AGENT_REACTIVATION_REQUIRED).
        if (activationFundingRequiredRef.current) {
            openDeposit();
            return;
        }
        mutate();
    }, [mutate, isPending, openDeposit]);

    const activateHip3 = useCallback(() => {
        if (isActivatingHip3) return;
        mutateActivateHip3();
    }, [mutateActivateHip3, isActivatingHip3]);

    // The durable half of the pending state. An agentSetup job the server still reports as active
    // means a reactivation is running, whether or not THIS tab started it and whether or not the
    // page has been reloaded since.
    const activeSetupJob = useMemo(() => {
        const candidate = jobs.find(
            (job) =>
                job.kind === 'agentSetup' &&
                isActiveSpeedJob(job) &&
                // `authorizationRequired` counts as active server-side, but it is waiting on the
                // user, not on the backend. Keeping it here is what made the pending banner
                // permanent.
                job.status !== 'authorizationRequired' &&
                !isSetupDeadEnd(job),
        );
        if (!candidate) return null;
        const startedAt = Date.parse(candidate.createdAt);
        // a job wedged in `queued` must not disable trading indefinitely
        if (
            Number.isFinite(startedAt) &&
            Date.now() - startedAt > REACTIVATION_STALL_AFTER_MS
        ) {
            return null;
        }
        return { job: candidate, startedAtMs: startedAt };
    }, [jobs]);

    // Adoption. `watchedJobId` is per-instance state that only the instance which fired the
    // mutation ever sets, so after a reload the run resumed VISUALLY — `activeSetupJob` keeps
    // `isReactivating` true — but nothing was left to notice it settling: no toast, no status
    // invalidation, the banner just sat there until something else refetched. Adopting the
    // server-reported job wires the terminal handshake back up for reloaded tabs (and for hook
    // instances that never clicked). Duplicate toasts are prevented by the module-level record.
    useEffect(() => {
        if (watchedJobId !== null || !activeSetupJob) return;
        const { jobId } = activeSetupJob.job;
        // a job the safety timer already released must not be re-adopted, or the two effects
        // would hand it back and forth forever.
        if (reportedSetupJobOutcomes.has(`release:${jobId}`)) return;
        setWatchedJobId(jobId);
    }, [watchedJobId, activeSetupJob]);

    // A parked setup job survives a reload, and after one no instance is watching it. Read straight
    // off the list, it tells a reloaded tab that the run is waiting for the user's signature (the
    // button resumes it), which is the truth this used to translate into a deposit instruction.
    const parkedSetupJobId = useMemo(
        () =>
            jobs.find(
                (job) =>
                    job.kind === 'agentSetup' &&
                    job.status === 'authorizationRequired',
            )?.jobId ?? null,
        [jobs],
    );
    // a parked job still inside its automatic-resume budget is work in progress, not a request
    // for the user: the poll keeps the parked row until it catches up, and between the signature
    // and that catch-up the banner used to flip to the manual copy over a run that was executing.
    const parkedAutoResuming =
        parkedSetupJobId !== null &&
        (parkResumeAttempts.get(parkedSetupJobId) ?? 0) <
            PARK_RESUME_MAX_ATTEMPTS;
    const awaitingSignature =
        parkedSetupJobId !== null &&
        !isMutatingReactivation &&
        !parkedAutoResuming;
    // a park found on a fresh mount (reload, or the tab that fired it is gone) is resumed on its
    // own as well: the resume answers with the signing challenge, so the wallet prompt is the whole
    // interaction. Retried a few times while the job stays parked (a request refused before it
    // left the browser must not spend the page load's only attempt); the button stays for a run
    // that keeps parking. Every decision is logged so a silent no-op is diagnosable from a console.
    const [parkResumeTick, setParkResumeTick] = useState(0);
    useEffect(() => {
        if (parkedSetupJobId === null) return;
        const gate = {
            jobId: parkedSetupJobId,
            isRealMode,
            hasAuthedApi: !!authedApi,
            isMutatingReactivation,
            reactivationInFlight,
            attempts: parkResumeAttempts.get(parkedSetupJobId) ?? 0,
        };
        if (
            !isRealMode ||
            !authedApi ||
            isMutatingReactivation ||
            reactivationInFlight
        ) {
            console.info(
                '[AgentReactivation] parked setup, not resuming yet',
                gate,
            );
            return;
        }
        if (gate.attempts >= PARK_RESUME_MAX_ATTEMPTS) {
            console.info(
                '[AgentReactivation] parked setup, attempts exhausted',
                gate,
            );
            if (!reportedSetupJobOutcomes.has(`park:${parkedSetupJobId}`)) {
                reportedSetupJobOutcomes.add(`park:${parkedSetupJobId}`);
                toast.error(AWAITING_SIGNATURE_MESSAGE, {
                    duration: REACTIVATE_ERROR_TOAST_MS,
                });
            }
            return;
        }
        const lastAt = parkResumeLastAtMs.get(parkedSetupJobId) ?? 0;
        const wait = PARK_RESUME_RETRY_MS - (Date.now() - lastAt);
        if (wait > 0) {
            const timer = setTimeout(
                () => setParkResumeTick((n) => n + 1),
                wait,
            );
            return () => clearTimeout(timer);
        }
        parkResumeAttempts.set(parkedSetupJobId, gate.attempts + 1);
        parkResumeLastAtMs.set(parkedSetupJobId, Date.now());
        reportedSetupJobOutcomes.add(`resume:${parkedSetupJobId}`);
        resumedParksAtMs.set(parkedSetupJobId, Date.now());
        console.info('[AgentReactivation] resuming parked setup', gate);
        mutate();
    }, [
        authedApi,
        isMutatingReactivation,
        isRealMode,
        mutate,
        parkResumeTick,
        parkedSetupJobId,
    ]);

    // Never activated at all: no agent has ever been approved for this wallet. The first press is
    // an activation, not a re-activation, and the copy says so.
    const firstActivation =
        isRealMode &&
        status !== undefined &&
        !status.walletMissing &&
        status.agentAddress === null &&
        status.validUntil === null;

    // The first deposit is what makes activation possible, so it also starts it, silently: the
    // wallet's signature prompt simply appears as the last step of depositing. Armed directly by
    // the deposit flow the moment its job settles (`armFirstActivation`), and as a fallback by a
    // deposit job observed going from active to settled in this page load. It fires as soon as the
    // balance read shows the funds, and gives up quietly if they never show.
    const [armedDeposit, setArmedDeposit] = useState<{
        jobId: string;
        expiresAtMs: number;
    } | null>(null);
    const armFirstActivation = useCallback(() => {
        const jobId = `deposit-flow:${Date.now()}`;
        autoActivatedDeposits.add(jobId);
        setArmedDeposit({
            jobId,
            expiresAtMs: Date.now() + AUTO_ACTIVATION_WINDOW_MS,
        });
    }, []);
    useEffect(() => {
        for (const job of jobs) {
            if (job.kind !== 'deposit') continue;
            const previous = observedDepositStatuses.get(job.jobId);
            observedDepositStatuses.set(job.jobId, job.status);
            if (
                previous !== undefined &&
                previous !== 'settled' &&
                job.status === 'settled' &&
                !autoActivatedDeposits.has(job.jobId)
            ) {
                autoActivatedDeposits.add(job.jobId);
                setArmedDeposit({
                    jobId: job.jobId,
                    expiresAtMs: Date.now() + AUTO_ACTIVATION_WINDOW_MS,
                });
            }
        }
    }, [jobs]);
    useEffect(() => {
        if (!armedDeposit) return;
        if (Date.now() > armedDeposit.expiresAtMs) {
            setArmedDeposit(null);
            return;
        }
        // nothing to do for a wallet that already has an agent, or one whose status is not in yet
        if (
            status === undefined ||
            isBalanceLoading ||
            numericBalance === null
        ) {
            return;
        }
        if (!firstActivation) {
            setArmedDeposit(null);
            return;
        }
        if (numericBalance < HYPERCORE_BOOTSTRAP_MINIMUM_USDC) return;
        // another instance (or this one) already fired: this arm is redundant, drop it
        if (isReactivatingRef.current || reactivationInFlight) {
            setArmedDeposit(null);
            return;
        }
        setArmedDeposit(null);
        // no announcement: the signature prompt is the deposit flow's last step
        mutate();
    }, [
        armedDeposit,
        firstActivation,
        isBalanceLoading,
        mutate,
        numericBalance,
        status,
    ]);

    // One-shot boundary refresh. `needsReactivation` flips server-side when the approval enters
    // the re-approval margin, and nothing pushes that to an open tab. Rather than polling (the
    // per-observer staggered-timer trap documented in useCustodialJobs.ts), schedule a single
    // invalidation for the moment the flip is due — armed only when it is less than a day away.
    const validUntilMs = statusQuery.data?.validUntil ?? null;
    useEffect(() => {
        if (validUntilMs === null) return;
        const delay = validUntilMs - AGENT_REAPPROVAL_MARGIN_MS - Date.now();
        if (delay <= 0 || delay >= AGENT_STATUS_BOUNDARY_TIMER_MAX_MS) return;
        const timer = setTimeout(() => {
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.agentStatus(),
            });
        }, delay);
        return () => clearTimeout(timer);
    }, [validUntilMs, queryClient]);

    const isReactivating =
        isPending ||
        isMutatingReactivation ||
        watchedJobId !== null ||
        activeSetupJob !== null ||
        parkedAutoResuming;
    // read inside the auto-activation effect without re-arming it on every pending flip
    isReactivatingRef.current = isReactivating;

    if (!isReactivating) {
        optimisticStartRef.current = null;
    }

    const reactivationStartedAtMs = !isReactivating
        ? null
        : activeSetupJob && Number.isFinite(activeSetupJob.startedAtMs)
            ? activeSetupJob.startedAtMs
            : optimisticStartRef.current;

    return {
        needsReactivation: isRealMode && (status?.needsReactivation ?? false),
        walletMissing: isRealMode && (status?.walletMissing ?? false),
        bootstrapRequired: isRealMode && (status?.bootstrapRequired ?? false),
        activationFundingRequired,
        builderApprovalStale:
            isRealMode && (status?.builderApprovalStale ?? false),
        agentAddress: status?.agentAddress ?? null,
        validUntil: status?.validUntil ?? null,
        /** No agent was ever approved: the button activates rather than re-activates. */
        firstActivation,
        /** A setup run is parked on the user's signature; pressing the button resumes it. */
        awaitingSignature,
        /** Deposit flow hook: a deposit just settled, activate as soon as the funds show. */
        armFirstActivation,
        isRealMode,
        // stays true until the job is terminal, so the button cannot be spammed while one is running
        isReactivating,
        reactivationStartedAtMs,
        reactivate,
        requiresHip3Activation:
            isRealMode && (status?.requiresHip3Activation ?? false),
        isActivatingHip3,
        activateHip3,
    };
}
